// src/App.js
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';
import NFTDashboard from './NFTDashboard';
import BuildTrackerDashboard from './BuildTrackerDashboard';
import BuildTrackerAdminDashboard from './BuildTrackerAdminDashboard';
import BuildTracker from './components/BuildTracker';
import { checkIfAdmin } from './api';
import walletManager from './utils/WalletManager';
import WalletSelectionModal from './components/WalletSelectionModal';
import ErrorModal from './ErrorModal';

// Lazy load NadscriptionsLaunchpad to prevent its AppKit from initializing
// until the user actually visits the /nadscriptions page
const NadscriptionsLaunchpad = lazy(() => import('./NadscriptionsLaunchpad'));

// Header component that hides on nadscriptions page
function AppHeader({ wallet, isAdmin }) {
  const location = useLocation();
  const isNadscriptionsPage = location.pathname === '/nadscriptions';
  
  if (isNadscriptionsPage) {
    return null;
  }
  
  return (
    <>
      <h1>DOCTORdripp Digital Collectibles</h1>
      {wallet && isAdmin && (
        <nav style={{ marginBottom: '1rem' }}>
          <Link 
            to="/" 
            className="rainbowBtn" 
            style={{ marginRight: '1rem', textDecoration: 'none', display: 'inline-block' }}
          >
            Public Dashboard
          </Link>
          <Link 
            to="/admin" 
            className="rainbowBtn" 
            style={{ marginRight: '1rem', textDecoration: 'none', display: 'inline-block' }}
          >
            Admin Dashboard
          </Link>
          <Link 
            to="/admin/build-tracker" 
            className="rainbowBtn" 
            style={{ textDecoration: 'none', display: 'inline-block' }}
          >
            🛠️ Build Tracker Admin
          </Link>
        </nav>
      )}
    </>
  );
}

// Wrapper that removes padding on nadscriptions page
function AppWrapper({ children }) {
  const location = useLocation();
  const isNadscriptionsPage = location.pathname === '/nadscriptions';
  
  if (isNadscriptionsPage) {
    return <>{children}</>;
  }
  
  return (
    <div style={{ padding: "20px" }} className="nftDashboardContainer">
      {children}
    </div>
  );
}

function App() {
  const [wallet, setWallet] = useState('');
  const [jwtToken, setJwtToken] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showSecurityAlert, setShowSecurityAlert] = useState(false);
  const [disconnectInstructions, setDisconnectInstructions] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  
  // NEW: Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalConfig, setErrorModalConfig] = useState({
    title: 'Error',
    message: 'An error occurred',
    type: 'error'
  });
  
  // NEW: Wallet selection modal state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWalletType, setSelectedWalletType] = useState(null);
  const [availableWallets, setAvailableWallets] = useState([]);

  // Check if this is a Shopify admin access (bypass Web3 gate)
  const isShopifyAdminAccess = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedded = urlParams.get('embedded') === 'true';
    const hasShopParam = urlParams.get('shop') && urlParams.get('shop').includes('.myshopify.com');
    const isAdminRoute = window.location.pathname.includes('/admin');
    const referer = document.referrer;
    const isFromShopify = referer && (referer.includes('admin.shopify.com') || referer.includes('.myshopify.com'));
    
    return (isEmbedded && hasShopParam && isAdminRoute) || isFromShopify;
  };

  // Suppress Rainbow wallet Chrome extension errors
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (event.reason && 
          (event.reason.message?.includes('chrome.runtime.sendMessage') || 
           event.reason.message?.includes('Extension ID') ||
           (event.reason.stack && event.reason.stack.includes('inpage.js')))) {
        console.warn('🌈 Suppressed Rainbow wallet Chrome extension error:', event.reason.message);
        event.preventDefault(); // Prevent the error from appearing in console
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Auto-set admin status for Shopify access
  useEffect(() => {
    if (isShopifyAdminAccess()) {
      console.log('🛠️ Shopify admin access detected, bypassing Web3 gate');
      setIsAdmin(true);
      setWallet('shopify-admin'); // Set a placeholder wallet to bypass checks
    }
  }, []);

  const dismissSecurityAlert = () => {
    setShowSecurityAlert(false);
    setDisconnectInstructions(null); // Clear instructions when dismissing alert
  };

  // NEW: User-friendly error handling function
  const showUserFriendlyError = (error, context = '') => {
    let title = 'Connection Error';
    let message = 'An unexpected error occurred. Please try again.';
    let type = 'error';

    // Extract the actual error message from nested error objects
    let actualErrorMessage = error.message || '';
    
    // Check for nested error messages (common with Ledger and other hardware wallets)
    if (error.data?.cause?.message) {
      actualErrorMessage = error.data.cause.message;
    } else if (error.cause?.message) {
      actualErrorMessage = error.cause.message;
    } else if (error.error?.message) {
      actualErrorMessage = error.error.message;
    }
    
    // If we still have the full error object as a string, try to extract just the message
    if (actualErrorMessage.includes('could not coalesce error') && actualErrorMessage.includes('"message":')) {
      try {
        const match = actualErrorMessage.match(/"message":\s*"([^"]+)"/);
        if (match && match[1]) {
          actualErrorMessage = match[1];
        }
      } catch (e) {
        // If parsing fails, keep the original message
      }
    }

    // Handle different error types
    if (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || actualErrorMessage?.includes('User rejected') || actualErrorMessage?.includes('user denied') || actualErrorMessage?.includes('User denied') || actualErrorMessage?.includes('User rejected the request') || actualErrorMessage?.includes('ethers-user-denied') || actualErrorMessage?.includes('action="signMessage"')) {
      // User cancelled either connection or signature
      title = 'Connection Cancelled';
      type = 'info';
      
      if (actualErrorMessage?.includes('signMessage') || actualErrorMessage?.includes('personal_sign') || actualErrorMessage?.includes('sign') || actualErrorMessage?.includes('ethers-user-denied') || actualErrorMessage?.includes('User rejected the request') || error.code === 'ACTION_REJECTED' || error.code === -32603) {
        message = 'You need to sign the message to authenticate and use the app. Please try connecting again and approve the signature request.';
      } else if (actualErrorMessage?.includes('requestAccounts') || actualErrorMessage?.includes('eth_requestAccounts')) {
        message = 'You need to connect your wallet to use the app. Please try again and approve the connection request.';
      } else {
        message = 'You cancelled the wallet connection. Please try again and approve both the connection and signature requests to use the app.';
      }
    } else if (actualErrorMessage?.includes('Please install')) {
      // Wallet not installed
      title = 'Wallet Not Found';
      type = 'warning';
      message = actualErrorMessage;
    } else if (actualErrorMessage?.includes('Authentication failed')) {
      // Backend authentication issue
      title = 'Authentication Error';
      type = 'error';
      message = 'There was a problem authenticating your wallet. Please try connecting again. If the problem persists, please contact support.';
    } else if (actualErrorMessage?.includes('network') || actualErrorMessage?.includes('connection')) {
      // Network issues
      title = 'Network Error';
      type = 'warning';
      message = 'There was a network connection problem. Please check your internet connection and try again.';
    } else if (actualErrorMessage?.includes('Unsupported wallet')) {
      // Unsupported wallet type
      title = 'Unsupported Wallet';
      type = 'warning';
      message = 'The selected wallet type is not supported. Please choose MetaMask or Coinbase Wallet.';
    } else if (actualErrorMessage === 'WALLET_CONFLICT_RAINBOW_METAMASK') {
      // Rainbow + MetaMask conflict
      title = 'Wallet Conflict Detected';
      type = 'warning';
      message = 'Rainbow Wallet has taken over MetaMask\'s connection. To use MetaMask, please disable the Rainbow Wallet extension temporarily, refresh the page, and try again. Alternatively, you can use Rainbow Wallet instead.';
    } else if (actualErrorMessage === 'WALLET_CONFLICT_RAINBOW_COINBASE') {
      // Rainbow + Coinbase conflict
      title = 'Multiple Wallets Connected';
      type = 'warning';
      message = 'You have both Rainbow and Coinbase wallets connected. To use Rainbow Wallet, please disconnect from Coinbase Wallet first, then try connecting to Rainbow again.';
    } else if (actualErrorMessage === 'RAINBOW_EXTENSION_ERROR') {
      // Rainbow extension communication error
      title = 'Rainbow Wallet Connection Issue';
      type = 'warning';
      message = 'There was a communication issue with Rainbow Wallet. Please refresh the page and try again. If the problem persists, try restarting your browser.';
    } else if (actualErrorMessage?.includes('chrome.runtime.sendMessage') || actualErrorMessage?.includes('Extension ID') || actualErrorMessage?.includes('extension communication error')) {
      // Other Chrome extension communication errors
      title = 'Wallet Extension Error';
      type = 'warning';
      message = 'There was a communication error with your wallet extension. Please refresh the page and try again.';
    } else if (actualErrorMessage?.includes('Ledger:') || actualErrorMessage?.includes('Ethereum app closed') || actualErrorMessage?.includes('Device is locked')) {
      // Ledger wallet errors
      title = 'Ledger Wallet Issue';
      type = 'warning';
      message = 'Your Ledger device needs attention. Please open the Ethereum app on your Ledger device and try again.';
    } else {
      // Generic error with more helpful message
      title = 'Connection Failed';
      message = `${actualErrorMessage || "Unknown error"}. Please try again or contact support if the problem persists.`;
    }

    setErrorModalConfig({ title, message, type });
    setShowErrorModal(true);
  };

  // Load available wallets on component mount
  useEffect(() => {
    const loadWallets = async () => {
      try {
        const wallets = await walletManager.getAvailableWallets();
        setAvailableWallets(wallets);
      } catch (error) {
        console.error('Error loading wallets:', error);
        setAvailableWallets([]);
      }
    };
    loadWallets();
  }, []);

  // NEW: Enhanced connect wallet with modal
  const initiateWalletConnection = async () => {
    // Use the pre-loaded available wallets
    if (availableWallets.length === 1) {
      connectWalletDirect(availableWallets[0].type);
    } else {
      // Show wallet selection modal
      setShowWalletModal(true);
    }
  };

  // NEW: Handle wallet selection from modal
  const handleWalletSelection = (walletType) => {
    setSelectedWalletType(walletType);
    connectWalletDirect(walletType);
  };

  // NEW: Enhanced connect function that works with both wallets
  const connectWalletDirect = async (walletType) => {
    try {
      setIsAuthenticating(true);
      
      // Step 1: Connect to selected wallet using WalletManager
      const connectionResult = await walletManager.connectWallet(walletType);
      const walletAddress = connectionResult.address;
      
      // Step 2: Create message to sign (same as before)
      const message = `Welcome to DOCTORdripp!\n\nSign this message to authenticate your wallet.\n\nTimestamp: ${Date.now()}`;
      
      // Step 3: Request signature using WalletManager
      const signature = await walletManager.signMessage(message);

      // Step 4: Authenticate with backend
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          signature,
          message,
          walletType // Add wallet type for backend debugging
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Auth Error - Status:', response.status);
        console.error('🔍 Auth Error - Response:', errorText);
        throw new Error(`Authentication failed: ${response.status} - ${errorText}`);
      }

      const { jwtToken } = await response.json();

      // Step 5: Set authenticated state (unchanged)
      setWallet(walletAddress);
      setJwtToken(jwtToken);
      setSelectedWalletType(walletType);
      
    } catch (error) {
      console.error("Authentication failed:", error);
      showUserFriendlyError(error, 'connection');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // LEGACY: Keep original connectWallet for backward compatibility
  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask.");
      return;
    }

    try {
      setIsAuthenticating(true);
      
      // Step 1: Connect to MetaMask
      const [walletAddress] = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      // Step 2: Create message to sign
      const message = `Welcome to DOCTORdripp!\n\nSign this message to authenticate your wallet.\n\nTimestamp: ${Date.now()}`;
      
      // Step 3: Request signature
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, walletAddress]
      });

      // Step 4: Authenticate with backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          signature,
          message
        })
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const { jwtToken } = await response.json();

      // Step 5: Set authenticated state
      setWallet(walletAddress);
      setJwtToken(jwtToken);
      
    } catch (error) {
      console.error("Authentication failed:", error);
      showUserFriendlyError(error, 'connection');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const disconnectWallet = async () => {
    // IMPORTANT: Get disconnect instructions BEFORE clearing wallet state
    const instructions = walletManager.getDisconnectInstructions();
    setDisconnectInstructions(instructions);
    
    try {
      // NEW: Use WalletManager for proper disconnect (now async)
      await walletManager.disconnect();
    } catch (error) {
      console.warn('Error during wallet disconnect:', error);
      // If normal disconnect fails, force cleanup
      walletManager.forceCleanup();
    }
    
    setWallet('');
    setJwtToken('');
    setSelectedWalletType(null);
    setShowSecurityAlert(true);
    
    // Hide alert after 60 seconds
    setTimeout(() => {
      setShowSecurityAlert(false);
      setDisconnectInstructions(null); // Clear instructions when hiding alert
    }, 60000);
  };

  useEffect(() => {
    if (wallet) {
      checkIfAdmin(wallet)
        .then(result => setIsAdmin(result))
        .catch(err => {
          console.error("Error checking admin status:", err);
          setIsAdmin(false);
        });
    } else {
      setIsAdmin(false);
    }
  }, [wallet]);

  return (
    <Router>
      <AppWrapper>
        <AppHeader wallet={wallet} isAdmin={isAdmin} />
        <Routes>
          <Route path="/" element={<NFTDashboard 
            wallet={wallet} 
            onConnectWallet={initiateWalletConnection} 
            onDisconnectWallet={disconnectWallet} 
            isAuthenticating={isAuthenticating} 
            showSecurityAlert={showSecurityAlert} 
            onDismissSecurityAlert={dismissSecurityAlert}
            selectedWalletType={selectedWalletType}
            disconnectInstructions={disconnectInstructions}
            isAdmin={isAdmin}
          />} />
          <Route path="/nadscriptions" element={
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0a0a', color: '#fff' }}>Loading Nadscriptions...</div>}>
              <NadscriptionsLaunchpad />
            </Suspense>
          } />
          <Route
            path="/admin"
            element={isAdmin ? <AdminDashboard wallet={wallet} jwtToken={jwtToken} /> : <Navigate to="/" replace />}
          />
          <Route
            path="/build-tracker"
            element={<BuildTrackerDashboard customerEmail={customerEmail} onEmailChange={setCustomerEmail} />}
          />
          <Route
            path="/admin/build-tracker"
            element={isAdmin ? <BuildTrackerAdminDashboard jwtToken={jwtToken} /> : <Navigate to="/" replace />}
          />
        </Routes>

        {/* Build Tracker Floating Header */}
        <BuildTracker 
          customerEmail={customerEmail}
          isVisible={customerEmail && !isAdmin} // Only show for regular users with email
        />

        {/* NEW: Wallet Selection Modal */}
        <WalletSelectionModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          onSelectWallet={handleWalletSelection}
          availableWallets={availableWallets}
        />

        {/* NEW: Error Modal for wallet connection errors */}
        <ErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={errorModalConfig.title}
          message={errorModalConfig.message}
          type={errorModalConfig.type}
        />
      </AppWrapper>
    </Router>
  );
}

export default App;
